import { Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Switch, { type SwitchProps } from '@material-ui/core/Switch';
import { ToggleOff, ToggleOn } from '@material-ui/icons';
import { Colors, getColorWithOpacity } from '@rentguru/commons-utils';
import clsx from 'clsx';
import { isNil } from 'lodash';
import { useState } from 'react';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 42,
    height: 26,
    padding: 0,
    margin: theme.spacing(0),
  },
  rootSmall: {
    width: 24,
    height: 20,
    overflow: 'visible',
  },
  rootBig: {
    width: 42,
    height: 26,
    overflow: 'visible',
  },
  switchBaseCommon: {
    padding: 1,
    color: Colors.PORCELAIN_GREY_3,
    '&$checked': {
      color: Colors.CLASSICAL_WHITE,
      '& + $track, & + $trackSmall, & + $trackBig': {
        backgroundColor: Colors.DODGER_BLUE,
        opacity: 1,
      },
    },
  },
  switchBaseSmall: {
    '&$checked': {
      transform: 'translateX(12px)',
    },
  },
  switchBaseBig: {
    '&$checked': {
      transform: 'translateX(14px)',
    },
  },
  oldSwitchBase: {
    width: '100%',
    height: '100%',
    padding: 0,
    '&$checked': {
      transform: 'translateX(0px)',
      '&:hover': {
        backgroundColor: `${Colors.DODGER_BLUE}10`,
      },
    },
  },
  thumb: {
    width: 24,
    height: 24,
    backgroundColor: Colors.CLASSICAL_WHITE,
  },
  thumbSmall: {
    width: 9,
    height: 9,
    margin: 0.5,
    backgroundColor: Colors.CLASSICAL_WHITE,
  },
  thumbBig: {
    width: 24,
    height: 24,
    margin: 1,
    backgroundColor: Colors.CLASSICAL_WHITE,
  },
  track: {
    backgroundColor: Colors.PORCELAIN_GREY_1,
    opacity: 1,
    transition: theme.transitions.create(['background-color', 'border']),
  },
  trackSmall: {
    borderRadius: 7,
    backgroundColor: Colors.PORCELAIN_GREY_1,
    height: 12,
    opacity: 1,
    transition: theme.transitions.create(['background-color', 'border']),
  },
  trackBig: {
    borderRadius: 14,
    backgroundColor: Colors.PORCELAIN_GREY_1,
    height: 28,
    opacity: 1,
    transition: theme.transitions.create(['background-color', 'border']),
  },
  trackNone: {
    display: 'none',
  },
  checked: {},
  focusVisible: {},
}));

interface CustomSwitchProps extends SwitchProps {
  switchOnText?: string;
  switchOffText?: string;
  disabledClasses?: string;
  dataTest?: string;
  disabled?: boolean;
  bigSwitch?: boolean;
  smallSwitch?: boolean;
  handleChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
}

export const CustomSwitch = ({
  switchOnText,
  switchOffText,
  disabledClasses,
  handleChange,
  dataTest,
  disabled,
  bigSwitch,
  smallSwitch,
  ...rest
}: CustomSwitchProps) => {
  const classes = useStyles();
  const { checked } = rest;
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const closeTooltip = () => {
    if (showTooltip) setShowTooltip(false);
  };

  const getRootClass = () => {
    if (bigSwitch) return clsx(classes.root, classes.rootBig);
    if (smallSwitch) return clsx(classes.root, classes.rootSmall);
    return classes.root;
  };

  const getSwitchBaseClass = () => {
    if (bigSwitch) return clsx(classes.switchBaseCommon, classes.switchBaseBig);
    if (smallSwitch) return clsx(classes.switchBaseCommon, classes.switchBaseSmall);
    return classes.oldSwitchBase;
  };

  const getThumbClass = () => {
    if (bigSwitch) return classes.thumbBig;
    if (smallSwitch) return classes.thumbSmall;
    return classes.thumb;
  };

  const getTrackClass = () => {
    if (bigSwitch) return classes.trackBig;
    if (smallSwitch) return classes.trackSmall;
    return classes.trackNone;
  };

  return (
    <Tooltip
      PopperProps={{
        disablePortal: true,
      }}
      onClose={closeTooltip}
      open={showTooltip}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      title={checked ? (switchOnText ?? '') : (switchOffText ?? '')}
    >
      <Switch
        focusVisibleClassName={classes.focusVisible}
        disableRipple
        {...(!bigSwitch &&
          !smallSwitch && {
            checkedIcon: (
              <ToggleOn
                style={{
                  fill: disabled ? getColorWithOpacity(Colors.DODGER_BLUE, 80) : Colors.DODGER_BLUE,
                  height: 30,
                  width: 30,
                }}
              />
            ),
            icon: (
              <ToggleOff
                style={{ fill: disabled ? Colors.PORCELAIN_GREY_3 : Colors.ROLLING_STONE_GREY, height: 30, width: 30 }}
              />
            ),
          })}
        classes={{
          root: getRootClass(),
          switchBase: getSwitchBaseClass(),
          thumb: getThumbClass(),
          track: getTrackClass(),
          checked: classes.checked,
          ...(isNil(disabledClasses) ? {} : { disabled: disabledClasses }),
        }}
        data-test={dataTest}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={closeTooltip}
        onChange={handleChange}
        disabled={disabled}
        {...rest}
      />
    </Tooltip>
  );
};
